import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Kanban, Eye, EyeOff, ArrowRight, Users, BarChart3, Target, Lock } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Identifiants incorrects');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#1B99E5] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full border-4 border-white"></div>
          <div className="absolute bottom-20 left-10 w-40 h-40 rounded-full border-4 border-white"></div>
          <div className="absolute top-1/2 left-1/3 w-20 h-20 rounded-full border-2 border-white"></div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Kanban size={20} className="text-white" />
            </div>
            <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
              PipelinePro
            </span>
          </div>
          <h1 className="text-white text-4xl font-bold leading-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gérez votre pipeline commercial comme un pro
          </h1>
          <p className="text-white/80 text-lg leading-relaxed">
            CRM moderne et intuitif pour équipes de vente performantes.
          </p>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-4">
          {[
            { icon: Target, label: 'Pipeline Kanban', desc: 'Visualisez vos devis' },
            { icon: Users, label: 'Contacts & Sociétés', desc: 'Gérez vos relations' },
            { icon: BarChart3, label: 'Rapports IA', desc: 'Analyses intelligentes' },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="bg-white/10 rounded-xl p-4">
              <Icon size={20} className="text-white mb-2" />
              <p className="text-white text-sm font-semibold">{label}</p>
              <p className="text-white/70 text-xs mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Right panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <div className="w-9 h-9 bg-[#1B99E5] rounded-xl flex items-center justify-center">
              <Kanban size={16} className="text-white" />
            </div>
            <span className="font-bold text-xl text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              PipelinePro
            </span>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 bg-[#1B99E5]/10 rounded-lg flex items-center justify-center">
                  <Lock size={16} className="text-[#1B99E5]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Connexion
                  </h2>
                  <p className="text-xs text-gray-400">Accès réservé aux collaborateurs</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="login-email" className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Email
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="vous@exemple.com"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  className="h-10 border-gray-200 focus:ring-2 focus:ring-[#1B99E5] focus:border-[#1B99E5]"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label htmlFor="login-password" className="text-sm font-medium text-gray-700 mb-1.5 block">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    className="h-10 border-gray-200 focus:ring-2 focus:ring-[#1B99E5] focus:border-[#1B99E5] pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-10 bg-[#1B99E5] hover:bg-[#0f86cc] text-white font-medium rounded-lg mt-2 active:scale-95"
                style={{ transition: 'background-color 150ms ease, transform 100ms ease' }}
                data-testid="login-submit-button"
              >
                {loading ? 'Connexion...' : (
                  <span className="flex items-center gap-2">Se connecter <ArrowRight size={16} /></span>
                )}
              </Button>
            </form>

            <div className="mt-5 pt-4 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-400">
                Pour obtenir un accès, contactez votre administrateur.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
